// ---------------------------------------------------------------------------
// AG-014 Phase 3 — Agency Pricing (wholesale vs retail) persistence schema.
// ---------------------------------------------------------------------------
// Story AG-014: "Agency billing charges agency wholesale while agency charges
// clients externally/retail; client billing status is tracked."
//
// Phase 1 (LANDED) shipped the pure-function classifier in
// `packages/platform/src/services/billing/ag-014-wholesale-retail-classifier.ts`.
// Phase 5 (LANDED) shipped the CQRS event-catalog vocabulary in
// `ag-014-agency-billing-event-catalog.ts`.
//
// This Phase 3 ships the persistence + service tier: the `agency_pricing`
// table records the wholesale price an agency pays the platform vs the
// retail price the agency charges its client, for a given plan, over a
// time window. The agency-commission-calculator service in
// `@ease/platform` reads this table over an arbitrary `[from, to)` period
// to compute per-client and aggregate commission accruals.
//
// Design rules locked here:
// - Tenant scoping: `tenant_id` is the AGENCY org id (the owner of the
//   pricing arrangement). `client_tenant_id` is the client this row covers.
// - One row per (agency, client, plan, effective_from). The unique index
//   enforces append-only history — editing a price = new row with later
//   `effective_from`, with the previous row optionally closed via
//   `effective_to`.
// - Currency: ISO-4217 string mandatory. Mixed-currency periods are
//   rejected by the service layer at calculation time.
// - All money is integer minor units (cents). NO numeric/decimal columns.
// - `wholesale_amount_cents <= retail_amount_cents` enforced by check
//   constraint so commission cannot go negative at insert time.
// - GDPR posture: contains no personal data; aggregated business metric.
//   Cascades by tenant_id alongside the rest of the agency lineage.
// ---------------------------------------------------------------------------

import {
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * `agency_pricing` — wholesale vs retail price for a given (agency, client,
 * plan) tuple, valid over a time window.
 *
 * The agency-commission-calculator service joins this table to the
 * authoritative subscription/invoice rows so commission accrual reads from
 * a deterministic price snapshot — not from a moving plan-catalog column
 * that could shift mid-period.
 */
export const agencyPricing = pgTable(
  "agency_pricing",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /**
     * Agency org id — the platform invoices THIS tenant at
     * `wholesale_amount_cents`. Tenant-scoped per CLAUDE.md security rules.
     */
    tenantId: uuid("tenant_id").notNull(),
    /** Client sub-tenant this pricing row applies to. */
    clientTenantId: uuid("client_tenant_id").notNull(),
    /** Plan id this pricing row is bound to (matches `plan_catalog.id`). */
    planId: uuid("plan_id").notNull(),
    /** Wholesale price (cents) — platform invoices agency at this amount. */
    wholesaleAmountCents: integer("wholesale_amount_cents").notNull(),
    /** Retail price (cents) — agency invoices client at this amount. */
    retailAmountCents: integer("retail_amount_cents").notNull(),
    /** ISO-4217 currency code (e.g. "USD"). */
    currency: text("currency").notNull(),
    /** Inclusive lower bound — when this pricing snapshot takes effect. */
    effectiveFrom: timestamp("effective_from", tz).notNull(),
    /**
     * Exclusive upper bound — when this pricing snapshot stops applying.
     * NULL means "currently in effect, no end scheduled".
     */
    effectiveTo: timestamp("effective_to", tz),
    /** Audit timestamps. */
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    /**
     * Append-only history: every (agency, client, plan, effective_from) is
     * unique. Editing a price = new row, not UPDATE in place.
     */
    uniqueIndex("agency_pricing_history_uq").on(
      table.tenantId,
      table.clientTenantId,
      table.planId,
      table.effectiveFrom,
    ),
    /**
     * Commission calculation hot path: scan all client pricing for an
     * agency over a period.
     */
    index("agency_pricing_tenant_period_idx").on(
      table.tenantId,
      table.effectiveFrom,
    ),
    /** Per-client drill-down. */
    index("agency_pricing_client_period_idx").on(
      table.clientTenantId,
      table.effectiveFrom,
    ),
    /** Commission ≥ 0 invariant: wholesale ≤ retail. */
    check(
      "agency_pricing_wholesale_le_retail_ck",
      sql`${table.wholesaleAmountCents} <= ${table.retailAmountCents}`,
    ),
    /** Money invariants: non-negative integer cents. */
    check(
      "agency_pricing_wholesale_nonneg_ck",
      sql`${table.wholesaleAmountCents} >= 0`,
    ),
    check(
      "agency_pricing_retail_nonneg_ck",
      sql`${table.retailAmountCents} >= 0`,
    ),
    /** Period sanity: from < to when to is present. */
    check(
      "agency_pricing_period_ordering_ck",
      sql`${table.effectiveTo} IS NULL OR ${table.effectiveFrom} < ${table.effectiveTo}`,
    ),
    /** Currency must be a 3-letter ISO-4217 code. */
    check(
      "agency_pricing_currency_iso_ck",
      sql`length(${table.currency}) = 3`,
    ),
  ],
);

export type AgencyPricingRow = typeof agencyPricing.$inferSelect;
export type AgencyPricingInsert = typeof agencyPricing.$inferInsert;
